import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface PaginationState {
  // Store pagination state for different pages
  warehouse: { pageIndex: number; search: string };
  demands: { pageIndex: number; id: string | null };
  entries: { pageIndex: number; id: string | null };
  exits: { pageIndex: number; id: string | null };
  codebook: { pageIndex: number; search: string };
  employees: { pageIndex: number; search: string };
  chooseCodebook: { pageIndex: number; search: string };
}

export interface PaginationActions {
  // Actions to update pagination state
  setWarehousePage: (pageIndex: number, search?: string) => void;
  setDemandsPage: (pageIndex: number, id: string) => void;
  setEntriesPage: (pageIndex: number, id: string) => void;
  setExitsPage: (pageIndex: number, id: string) => void;
  setCodebookPage: (pageIndex: number, search?: string) => void;
  setEmployeesPage: (pageIndex: number, search?: string) => void;
  setChooseCodebookPage: (pageIndex: number, search?: string) => void;
  // Reset pagination for specific page
  resetWarehousePage: () => void;
  resetDemandsPage: (id: string) => void;
  resetEntriesPage: (id: string) => void;
  resetExitsPage: (id: string) => void;
  resetCodebookPage: () => void;
  resetEmployeesPage: () => void;
  resetChooseCodebookPage: () => void;
}

const usePaginationStore = create<PaginationState & PaginationActions>()(
  persist(
    (set, get) => ({
      // Initial state
      warehouse: { pageIndex: 0, search: "" },
      demands: { pageIndex: 0, id: null },
      entries: { pageIndex: 0, id: null },
      exits: { pageIndex: 0, id: null },
      codebook: { pageIndex: 0, search: "" },
      employees: { pageIndex: 0, search: "" },
      chooseCodebook: { pageIndex: 0, search: "" },

      // Actions
      setWarehousePage: (pageIndex: number, search: string = "") => {
        const currentState = get().warehouse;
        set({
          warehouse: {
            pageIndex,
            search: search !== undefined ? search : currentState.search,
          },
        });
      },

      setDemandsPage: (pageIndex: number, id: string) => {
        set({
          demands: { pageIndex, id },
        });
      },

      setEntriesPage: (pageIndex: number, id: string) => {
        set({
          entries: { pageIndex, id },
        });
      },

      setExitsPage: (pageIndex: number, id: string) => {
        set({
          exits: { pageIndex, id },
        });
      },

      setCodebookPage: (pageIndex: number, search: string = "") => {
        const currentState = get().codebook;
        set({
          codebook: {
            pageIndex,
            search: search !== undefined ? search : currentState.search,
          },
        });
      },

      setEmployeesPage: (pageIndex: number, search: string = "") => {
        const currentState = get().employees;
        set({
          employees: {
            pageIndex,
            search: search !== undefined ? search : currentState.search,
          },
        });
      },

      setChooseCodebookPage: (pageIndex: number, search: string = "") => {
        const currentState = get().chooseCodebook;
        set({
          chooseCodebook: {
            pageIndex,
            search: search !== undefined ? search : currentState.search,
          },
        });
      },

      // Reset actions
      resetWarehousePage: () => {
        set({
          warehouse: { pageIndex: 0, search: "" },
        });
      },

      resetDemandsPage: (id: string) => {
        set({
          demands: { pageIndex: 0, id },
        });
      },

      resetEntriesPage: (id: string) => {
        set({
          entries: { pageIndex: 0, id },
        });
      },

      resetExitsPage: (id: string) => {
        set({
          exits: { pageIndex: 0, id },
        });
      },

      resetCodebookPage: () => {
        set({
          codebook: { pageIndex: 0, search: "" },
        });
      },

      resetEmployeesPage: () => {
        set({
          employees: { pageIndex: 0, search: "" },
        });
      },

      resetChooseCodebookPage: () => {
        set({
          chooseCodebook: { pageIndex: 0, search: "" },
        });
      },
    }),
    {
      name: "warehouse-pagination-storage",
      // Only persist pagination state, not search terms
      partialize: (state) => ({
        warehouse: { pageIndex: state.warehouse.pageIndex },
        demands: { pageIndex: state.demands.pageIndex, id: state.demands.id },
        entries: { pageIndex: state.entries.pageIndex, id: state.entries.id },
        exits: { pageIndex: state.exits.pageIndex, id: state.exits.id },
        codebook: { pageIndex: state.codebook.pageIndex },
        employees: { pageIndex: state.employees.pageIndex },
        chooseCodebook: { pageIndex: state.chooseCodebook.pageIndex },
      }),
    },
  ),
);

export default usePaginationStore;
